import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomDialog from "../../modals/CustomDialog/CustomDialog";
import { Button } from "../../forms/Buttons/Button";
import { copyFolderMatrix, getFolderMatrix } from "../../../services/questionService";
import { getApiErrorCode, getApiErrorMessage } from "../../../utils/apiError";
import { isCanceledError } from "../../../utils/examError";
import { compareGrades, gradeLabel, gradeOf, toNumber } from "./matrixModel";
import { Spinner, secondaryButtonClass } from "./MatrixParts";

// The subject folders a matrix can be copied from: the other folders of the same catalog subject (the other
// stream's, or the same subject in another exam), the same exam's first. `folders` is GET question_folders/.
export function copySources(folder, folders) {
  const examName = new Map((folders || []).filter((f) => !f.parent).map((f) => [f.uuid, f.name]));
  return (folders || [])
    .filter((f) => f.parent && f.uuid !== folder?.uuid && f.subject?.uuid && f.subject.uuid === folder?.subject?.uuid)
    .map((f) => ({ folder: f, label: [examName.get(f.parent), f.name].filter(Boolean).join(" · ") }))
    .sort((a, b) => (b.folder.parent === folder?.parent) - (a.folder.parent === folder?.parent) || a.label.localeCompare(b.label));
}

// The copy's refusals in words: another catalog subject, or a source the user cannot see.
export const copyErrorMessage = (t, err) => {
  if (getApiErrorCode(err) === "subject_mismatch") return t("folders.matrix.copy_mismatch");
  if (err?.response?.data?.source) return t("folders.matrix.copy_no_access");
  return getApiErrorMessage(t, err, "folders.matrix.copy_failed");
};

/**
 * What the sources' matrices have, read while `enabled`: {[folder uuid]: {rows, grades}}, `rows` null when the
 * matrix could not be read; a source not read yet has no entry. Another `attempt` reads them again, the entries
 * staying meanwhile (so nothing flickers).
 */
export function useSourceSummaries(sources, enabled, attempt = 0) {
  const [summaries, setSummaries] = useState({});
  const key = sources.map(({ folder: f }) => f.uuid).join(",");
  useEffect(() => {
    if (!enabled || !key) return undefined;
    const uuids = key.split(",");
    setSummaries((current) => Object.fromEntries(Object.entries(current).filter(([uuid]) => uuids.includes(uuid))));
    const controller = new AbortController();
    uuids.forEach((uuid) =>
      getFolderMatrix(uuid, { signal: controller.signal })
        .then((matrix) => {
          const rows = matrix?.rows || [];
          const grades = [...new Set(rows.map(gradeOf))].sort(compareGrades);
          setSummaries((current) => ({ ...current, [uuid]: { rows: rows.length, grades } }));
        })
        .catch((err) => {
          if (!isCanceledError(err)) setSummaries((current) => ({ ...current, [uuid]: { rows: null, grades: [] } }));
        })
    );
    return () => controller.abort();
  }, [key, enabled, attempt]);
  return summaries;
}

// "8 жол · 10-сынып, 11-сынып" of a source's matrix (`summary` of useSourceSummaries).
export function summaryText(t, summary) {
  if (!summary) return "";
  if (summary.rows === null) return t("folders.matrix.copy_source_unknown");
  if (!summary.rows) return t("folders.matrix.copy_source_empty");
  return `${t("folders.matrix.rows_n", { count: summary.rows })} · ${summary.grades.map((grade) => gradeLabel(t, grade)).join(", ")}`;
}

export const hasRows = (summary) => toNumber(summary?.rows) > 0;

// The first source with rows in the list's order (the same exam's other stream first); undefined while one before
// it is still being read, so a quicker answer from further down does not win.
export function firstWithRows(sources, summaries) {
  for (const { folder: f } of sources) {
    const summary = summaries[f.uuid];
    if (!summary) return undefined;
    if (hasRows(summary)) return f.uuid;
  }
  return undefined;
}

/**
 * Replaces the subject's matrix (`folder`) with a copy of another subject folder's of the same catalog subject.
 * Each source shows what its matrix has (`summaries` of useSourceSummaries); one not read yet or empty cannot be
 * picked (so the focus never rests on a choice that is then disabled), and the ones with rows come first.
 * `rowsCount` is how many rows the copy replaces. `onCopied(matrix, source)` gets the new matrix.
 */
export default function CopyMatrixDialog({ isOpen, folder, folders, summaries = {}, rowsCount, onClose, onCopied }) {
  const [t] = useTranslation();
  const sources = useMemo(() => copySources(folder, folders), [folder, folders]);
  // The order is set as the dialog opens, the sources with rows first, and kept: a summary coming in later does not
  // move the radios (nor the focus on one).
  const summariesRef = useRef(summaries);
  summariesRef.current = summaries;
  const listed = useMemo(
    () => (isOpen ? [...sources].sort((a, b) => hasRows(summariesRef.current[b.folder.uuid]) - hasRows(summariesRef.current[a.folder.uuid])) : sources),
    [isOpen, sources]
  );
  const allEmpty = sources.length > 0 && sources.every(({ folder: f }) => summaries[f.uuid]?.rows === 0);
  const [source, setSource] = useState("");
  const [copying, setCopying] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setError("");
    setSource("");
  }, [isOpen]);

  // The first source with rows is picked once the ones before it are read.
  useEffect(() => {
    if (!isOpen || source) return;
    const first = firstWithRows(sources, summaries);
    if (first) setSource(first);
  }, [isOpen, source, sources, summaries]);

  const copy = async () => {
    if (!source || copying) return;
    setCopying(true);
    setError("");
    try {
      const matrix = await copyFolderMatrix(folder.uuid, source);
      onCopied(matrix, sources.find((s) => s.folder.uuid === source));
    } catch (err) {
      setError(copyErrorMessage(t, err));
    } finally {
      setCopying(false);
    }
  };

  return (
    // z-30: over the edit mode's sticky Cancel / Save bar of phones (z-20), which the dialog opens from ("⋯").
    <CustomDialog isOpen={isOpen} closeModal={() => !copying && onClose()} headerText={t("folders.matrix.copy_title")} maxWidth="max-w-lg" zIndex="z-30">
      <div className="mt-2 space-y-4 text-left">
        <p className="text-sm text-slate-600">{t("folders.matrix.copy_hint")}</p>
        {!sources.length ? (
          <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">{t("folders.matrix.copy_none")}</p>
        ) : allEmpty ? (
          <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">{t("folders.matrix.copy_all_empty")}</p>
        ) : (
          <fieldset disabled={copying}>
            <legend className="mb-1.5 block text-sm font-medium text-slate-700">{t("folders.matrix.copy_source")}</legend>
            <div className="space-y-2">
              {listed.map(({ folder: f, label }) => {
                const summary = summaries[f.uuid];
                // Not read yet or empty: not a choice (a matrix that could not be read still is: the copy may work).
                const off = !summary || summary.rows === 0;
                return (
                  <label
                    key={f.uuid}
                    className={`flex items-start gap-3 rounded-lg px-3 py-2.5 ring-1 ring-inset transition duration-200 ${
                      source === f.uuid ? "bg-blue-50 ring-blue-600" : "bg-white ring-slate-200"
                    } ${off ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:bg-slate-50"}`}
                  >
                    <input
                      type="radio"
                      name="matrix-copy-source"
                      checked={source === f.uuid}
                      disabled={off}
                      onChange={() => setSource(f.uuid)}
                      className="mt-0.5 h-4 w-4 shrink-0 border-slate-300 text-blue-600 focus:ring-blue-600"
                    />
                    <span className="min-w-0">
                      <span className="block break-words text-sm font-medium text-slate-900">{label}</span>
                      <span className="flex items-center gap-1.5 text-xs text-slate-500">
                        {summary ? summaryText(t, summary) : <Spinner className="h-3 w-3" />}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        )}
        {rowsCount > 0 && source && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900 ring-1 ring-inset ring-amber-600/20">
            {t("folders.matrix.copy_replace_warning", { count: rowsCount })}
          </p>
        )}
        {error && (
          <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </p>
        )}
      </div>
      {/* Copy is the one main button; Cancel is quiet beside it (on phones both share the row). */}
      <div className="mt-6 flex justify-end gap-2">
        <button type="button" onClick={onClose} disabled={copying} className={secondaryButtonClass}>
          {t("buttons.cancel")}
        </button>
        <Button
          type="button"
          color="primary"
          text={t("folders.matrix.copy_confirm")}
          onClick={copy}
          loading={copying}
          disabled={!source || copying}
          className="max-md:h-11 max-md:flex-1"
        />
      </div>
    </CustomDialog>
  );
}
